import { Link } from "react-router-dom";
import { Phone, ArrowUpRight, ShieldCheck, Lock, KeyRound, Check, Star } from "lucide-react";
import { Overline, SectionHeader } from "@/components/Section";
import CTABand from "@/components/CTABand";

const IMG = "https://images.pexels.com/photos/20901468/pexels-photo-20901468.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const Remplacement = () => {
    return (
        <>
            <section className="relative border-b border-black/10 grid-bg" data-testid="remplacement-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-16 lg:pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-7 fade-up fade-up-1">
                            <Overline className="mb-5">◉ Service · Sécurité</Overline>
                            <h1 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                                Remplacement<br />de serrure &<br /><span className="text-[#E63946]">porte blindée.</span>
                            </h1>
                            <p className="mt-7 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl">
                                Pose de serrures certifiées A2P, cylindres anti-effraction, blocs-portes blindés.
                                Atelier Beauchamp vous conseille la solution la mieux adaptée à votre porte, votre budget et votre niveau de risque.
                            </p>
                            <div className="mt-9 flex flex-col sm:flex-row gap-3">
                                <a href="tel:+33649501843" data-testid="remplacement-call-btn" className="inline-flex items-center justify-between gap-4 bg-[#E63946] hover:bg-[#D62828] text-white px-6 py-4 transition-colors">
                                    <span className="flex items-center gap-3"><Phone className="h-5 w-5" strokeWidth={2.4} /><span className="font-display font-bold text-lg tracking-tight">06 49 50 18 43</span></span>
                                    <ArrowUpRight className="h-5 w-5" />
                                </a>
                                <Link to="/contact" className="inline-flex items-center justify-between gap-4 bg-[#0A0A0A] text-white px-6 py-4 hover:bg-zinc-800 transition-colors">
                                    <span className="font-semibold text-sm uppercase tracking-[0.15em]">Devis gratuit</span>
                                    <ArrowUpRight className="h-5 w-5" />
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5 fade-up fade-up-2">
                            <img src={IMG} alt="Serrure haute sécurité" className="w-full h-[380px] sm:h-[480px] object-cover border border-black/10" loading="eager" />
                        </div>
                    </div>
                </div>
            </section>

            {/* TYPES DE SERRURES */}
            <section className="py-20 lg:py-28 bg-white border-b border-black/10" data-testid="remplacement-types">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeader
                        kicker="◉ Solutions"
                        title="Le bon niveau de sécurité, au bon prix."
                        lead="De la simple serrure standard au bloc-porte blindé multipoints, nous installons et remplaçons toutes les marques reconnues."
                    />
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            { icon: KeyRound, t: "Cylindre standard", d: "Remplacement rapide après emménagement, perte de clés ou usure normale.", f: ["Pose immédiate", "Toutes marques", "Reproduction de clés"] },
                            { icon: Lock, t: "Cylindre A2P", d: "Cylindres anti-effraction certifiés (A2P*, **, ***) résistants au crochetage et au perçage.", f: ["Anti-perçage / anti-crochetage", "Carte de propriété", "Garantie fabricant"] },
                            { icon: ShieldCheck, t: "Serrure multipoints", d: "Serrures 3, 5 ou 7 points pour porte d'entrée — sécurité renforcée et homologation assurance.", f: ["3 à 7 points de fermeture", "Compatible normes assurances", "Pose soignée"] },
                        ].map((c) => (
                            <div key={c.t} className="bg-white border border-black/10 p-7 sm:p-9 hover:-translate-y-1 hover:border-black transition-all duration-200" data-testid={`remplacement-type-${c.t}`}>
                                <div className="h-12 w-12 grid place-items-center bg-[#0A0A0A] text-white">
                                    <c.icon className="h-5 w-5" strokeWidth={2.2} />
                                </div>
                                <h3 className="mt-7 font-display font-bold text-xl uppercase tracking-tight">{c.t}</h3>
                                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{c.d}</p>
                                <ul className="mt-6 space-y-2 text-sm">
                                    {c.f.map((f) => (
                                        <li key={f} className="flex items-start gap-2.5"><Check className="h-4 w-4 text-[#E63946] mt-0.5 flex-shrink-0" strokeWidth={2.6} /><span>{f}</span></li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PORTE BLINDEE */}
            <section className="py-20 lg:py-28" data-testid="remplacement-blindee">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-6">
                            <Overline className="mb-4">◉ Sécurité maximale</Overline>
                            <h2 className="font-display font-bold uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                                Pose de porte<br />blindée sur mesure.
                            </h2>
                            <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed">
                                Atelier Beauchamp installe des blocs-portes blindés certifiés, sur mesure, adaptés à votre logement.
                                Nous prenons en charge l'étude technique, la dépose de l'ancienne porte, la pose et les finitions.
                            </p>
                            <ul className="mt-7 space-y-3">
                                {[
                                    "Blocs-portes certifiés BP1, BP2, BP3",
                                    "Serrures multipoints A2P intégrées",
                                    "Isolation thermique et phonique renforcée",
                                    "Esthétique soignée (bois, laqué, métal)",
                                    "Conforme aux exigences des assurances",
                                ].map((t) => (
                                    <li key={t} className="flex items-start gap-3">
                                        <Star className="h-4 w-4 text-[#E63946] mt-1" strokeWidth={2.6} />
                                        <span className="text-zinc-700">{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="lg:col-span-6">
                            <div className="border border-black/10 bg-white">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-black/10 bg-zinc-50">
                                            <th className="text-left p-5 font-display font-bold uppercase text-xs tracking-[0.15em]">Norme</th>
                                            <th className="text-left p-5 font-display font-bold uppercase text-xs tracking-[0.15em]">Niveau</th>
                                            <th className="text-left p-5 font-display font-bold uppercase text-xs tracking-[0.15em]">Usage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            ["A2P *", "Standard", "Logement classique"],
                                            ["A2P **", "Renforcé", "Zone à risque modéré"],
                                            ["A2P ***", "Maximal", "Coffre, zone sensible"],
                                            ["BP1", "Anti-effraction 5 min", "Appartement standard"],
                                            ["BP2", "Anti-effraction 10 min", "Logement urbain"],
                                            ["BP3", "Anti-effraction 15 min", "Risque élevé"],
                                        ].map((r) => (
                                            <tr key={r[0]} className="border-b border-black/10 last:border-b-0 hover:bg-zinc-50 transition-colors">
                                                <td className="p-5 font-display font-bold">{r[0]}</td>
                                                <td className="p-5 text-zinc-700">{r[1]}</td>
                                                <td className="p-5 text-zinc-600">{r[2]}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="mt-4 text-xs uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                                Conseil personnalisé selon votre porte, votre quartier et votre assurance.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <CTABand title="Vous voulez renforcer votre sécurité ?" subtitle="Demandez un devis gratuit pour une serrure A2P ou une porte blindée — étude offerte." />
        </>
    );
};

export default Remplacement;
